import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  type FirestoreError,
  type Unsubscribe,
} from "firebase/firestore";
import { db } from "@/firebase/app";
import {
  decodeUserNotificationSnapshot,
  type UserNotification,
} from "@/models/userNotification";

const USER_NOTIFICATIONS_COLLECTION = "userNotifications";
export const NOTIFICATION_LIST_LIMIT = 200;
export const UNREAD_NOTIFICATION_LIMIT = 100;

function boundedLimit(value: number | undefined, fallback: number, maximum: number): number {
  if (value === undefined) return fallback;
  if (!Number.isInteger(value) || value < 1) {
    throw new RangeError("Notification limit must be a positive integer.");
  }
  return Math.min(value, maximum);
}

export async function listNotifications(
  userID: string,
  requestedLimit = NOTIFICATION_LIST_LIMIT,
): Promise<UserNotification[]> {
  const snapshot = await getDocs(query(
    collection(db, USER_NOTIFICATIONS_COLLECTION),
    where("userID", "==", userID),
    orderBy("createdAt", "desc"),
    limit(boundedLimit(requestedLimit, NOTIFICATION_LIST_LIMIT, NOTIFICATION_LIST_LIMIT)),
  ));

  return snapshot.docs.flatMap((document) => {
    const notification = decodeUserNotificationSnapshot(document);
    return notification ? [notification] : [];
  });
}

export async function fetchNotification(notificationID: string): Promise<UserNotification | null> {
  const snapshot = await getDoc(doc(db, USER_NOTIFICATIONS_COLLECTION, notificationID));
  return decodeUserNotificationSnapshot(snapshot);
}

export async function markNotificationRead(notificationID: string): Promise<void> {
  await updateDoc(doc(db, USER_NOTIFICATIONS_COLLECTION, notificationID), {
    readAt: serverTimestamp(),
  });
}

export async function deleteNotification(notificationID: string): Promise<void> {
  await deleteDoc(doc(db, USER_NOTIFICATIONS_COLLECTION, notificationID));
}

export interface UnreadNotificationSnapshot {
  notifications: UserNotification[];
  count: number;
  capped: boolean;
}

export function listenUnreadNotifications(
  userID: string,
  onChange: (snapshot: UnreadNotificationSnapshot) => void,
  onError?: (error: FirestoreError) => void,
): Unsubscribe {
  const unreadQuery = query(
    collection(db, USER_NOTIFICATIONS_COLLECTION),
    where("userID", "==", userID),
    where("readAt", "==", null),
    limit(UNREAD_NOTIFICATION_LIMIT),
  );

  return onSnapshot(
    unreadQuery,
    (snapshot) => {
      const notifications = snapshot.docs.flatMap((document) => {
        const notification = decodeUserNotificationSnapshot(document);
        return notification ? [notification] : [];
      });
      onChange({
        notifications,
        count: snapshot.size,
        capped: snapshot.size >= UNREAD_NOTIFICATION_LIMIT,
      });
    },
    onError,
  );
}

export function listenUnreadCount(
  userID: string,
  onChange: (count: number) => void,
  onError?: (error: FirestoreError) => void,
): Unsubscribe {
  return listenUnreadNotifications(
    userID,
    ({ count }) => onChange(count),
    onError,
  );
}
